import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatCurrency, getCurrentFinancialYear } from "../../../../utils/commonUtil";
import EditTaxAndDepreciation from "../../MyCashFlow/EditTaxAndDepreciation";
interface ITaxAndDepreciation {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  type: string;
  depreciation: number;
  taxRefund: number;
  landTax: number;
}

const CashFlowTaxAndDepreciation: React.FC<{
  cashFlowData: any;
  year: string;
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
  isShowingTotal?: boolean;
}> = ({ cashFlowData, year, isEditable, setIsEditable, isShowingTotal }) => {
  const [transformedData, setTransformedData] = useState<ITaxAndDepreciation[]>([]);
  const currentYear = "20" + Number(getCurrentFinancialYear()).toString();

  useEffect(() => {
    if (cashFlowData) {
      const actual = isShowingTotal
        ? cashFlowData.CashflowDepreciation?.Actuals?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowDepreciationPortion?.Actuals?.find((item: any) => item.Year === year) || {};
      const forecast = isShowingTotal
        ? cashFlowData.CashflowDepreciation?.Forecast?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowDepreciationPortion?.Forecast?.find((item: any) => item.Year === year) || {};
      const variance = isShowingTotal
        ? cashFlowData.CashflowDepreciation?.Variance?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowDepreciationPortion?.Variance?.find((item: any) => item.Year === year) || {};

      setTransformedData(
        [
          { type: "Forecast", data: forecast },
          { type: "Actuals", data: actual },
          { type: "Variance", data: variance },
        ].map(({ type, data }: { type: string; data: any }) => ({
          street: cashFlowData.PortfolioTrackerDetailLabel.StreetAddress || "",
          suburb: cashFlowData.PortfolioTrackerDetailLabel.Suburb || "",
          state: cashFlowData.PortfolioTrackerDetailLabel.State || "",
          postcode: cashFlowData.PortfolioTrackerDetailLabel?.Postcode || "",
          country: cashFlowData.PortfolioTrackerDetailLabel?.Country || "",
          type,
          depreciation: Number(data.Depreciation) || 0,
          taxRefund: Number(data.TaxRebate) || 0,
          landTax: Number(data.LandTax) || 0,
        }))
      );
    }
  }, [cashFlowData, year]);

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatCurrency(-value)})` : formatCurrency(value)}
    </span>
  );

  return (
    <>
      {isEditable ? (
        <EditTaxAndDepreciation
          cashFlowData={cashFlowData}
          setIsEditable={setIsEditable}
          onCancel={() => setIsEditable(false)}
          portfolioTrackerId={cashFlowData.PortfolioTrackerDetailLabel.PortfolioTrackerId}
          propertyName={`${cashFlowData.PortfolioTrackerDetailLabel.StreetAddress}, ${cashFlowData.PortfolioTrackerDetailLabel.Suburb}, ${cashFlowData.PortfolioTrackerDetailLabel.State}, ${cashFlowData.PortfolioTrackerDetailLabel.Postcode} (${cashFlowData.PortfolioTrackerDetailLabel.Country})`}
        />
      ) : (
        transformedData && (
          <DataTable
            value={transformedData}
            rowGroupMode="rowspan"
            groupRowsBy="street"
            sortField="street"
            sortMode="single"
            sortOrder={1}
            size="small"
          >
            <Column
              field="street"
              header="Address/property name"
              body={(rowData) => (
                <div className="flex items-center">
                  <span>{`${rowData.street}, ${rowData.suburb}, ${rowData.state}, ${rowData.postcode} (${rowData.country})`}</span>
                </div>
              )}
            />
            <Column field="type" header="Type" body={(rowData) => rowData.type} />
            <Column
              field="depreciation"
              header="Depreciation"
              body={(rowData) =>
                !(Number(currentYear) >= Number(year) || rowData.type == "Forecast")
                  ? ""
                  : currencyTemplate(rowData.depreciation)
              }
              align="right"
            />
            <Column
              field="taxRefund"
              header="Tax refund"
              body={(rowData) =>
                !(Number(currentYear) >= Number(year) || rowData.type == "Forecast")
                  ? ""
                  : currencyTemplate(rowData.taxRefund)
              }
              align="right"
            />
            <Column
              field="landTax"
              header="Land tax"
              body={(rowData) =>
                !(Number(currentYear) >= Number(year) || rowData.type == "Forecast")
                  ? ""
                  : currencyTemplate(rowData.landTax)
              }
              align="right"
            />
          </DataTable>
        )
      )}
    </>
  );
};

export default CashFlowTaxAndDepreciation;
